import { useState } from "react";
import { Search, Users, Lock, Globe, ChevronDown, Calendar, Plus, BookOpen, Gamepad2, Code, Dumbbell, Palette } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useToast } from "../../hooks/use-toast";





const categories = ["All", "Study", "Technology", "Social", "Sports", "Creative", "Gaming"];

const categoryIcons = {
  Study: BookOpen,
  Technology: Code,
  Social: Users,
  Sports: Dumbbell,
  Creative: Palette,
  Gaming: Gamepad2
};

const allGroups = [
{ id: 1, name: "Calculus Study Group", category: "Study", members: 12, bio: "Preparing together for Calculus II midterm. Daily problem solving sessions.", isPrivate: false, initials: "CS", creator: "Ali A.", createdAt: "2 days ago", events: [
  { title: "Problem Set Review", date: "Mar 14, 2026", location: "Library Room 3", attendees: 8 }]
},
{ id: 2, name: "Baku Tech Meetup", category: "Technology", members: 45, bio: "Informal meetups for tech enthusiasts in Baku. Talks, demos, and networking.", isPrivate: false, initials: "BT", creator: "Rashad H.", createdAt: "1 week ago", events: [
  { title: "AI Show & Tell", date: "Mar 20, 2026", location: "ADA Innovation Hub", attendees: 30 },
  { title: "Startup Pitch Night", date: "Apr 2, 2026", location: "Impact Hub", attendees: 50 }]
},
{ id: 3, name: "Weekend Hikers", category: "Sports", members: 28, bio: "Weekend hiking trips around Azerbaijan. All fitness levels welcome!", isPrivate: false, initials: "WH", creator: "Nigar A.", createdAt: "3 weeks ago", events: [
  { title: "Shahdag Mountain Hike", date: "Mar 22, 2026", location: "Shahdag", attendees: 15 }]
},
{ id: 4, name: "Film Discussion Club", category: "Creative", members: 18, bio: "Weekly movie nights followed by discussion. We explore world cinema and indie films.", isPrivate: false, initials: "FD", creator: "Sabina R.", createdAt: "5 days ago", events: [] },
{ id: 5, name: "IELTS Prep Squad", category: "Study", members: 22, bio: "Preparing for IELTS together — practice speaking, writing reviews, and mock tests.", isPrivate: true, initials: "IP", creator: "Kamran H.", createdAt: "1 month ago", events: [
  { title: "Speaking Mock Test", date: "Mar 16, 2026", location: "Online (Zoom)", attendees: 10 }]
},
{ id: 6, name: "Valorant Scrims", category: "Gaming", members: 35, bio: "Daily scrims and ranked queue together. Looking for dedicated players.", isPrivate: false, initials: "VS", creator: "Elvin M.", createdAt: "2 weeks ago", events: [
  { title: "5v5 Scrim Night", date: "Mar 15, 2026", location: "Discord", attendees: 20 }]
},
{ id: 7, name: "Freelance Designers", category: "Creative", members: 15, bio: "Share work, get feedback, find collaborators. UI/UX, graphic design, illustration.", isPrivate: false, initials: "FD", creator: "Aysel H.", createdAt: "4 days ago", events: [] },
{ id: 8, name: "Startup Founders AZ", category: "Technology", members: 32, bio: "Connect with fellow founders, share experiences, find co-founders and early users.", isPrivate: true, initials: "SF", creator: "Tural K.", createdAt: "2 months ago", events: [
  { title: "Founder Coffee Chat", date: "Mar 18, 2026", location: "Brew Café, Baku", attendees: 12 }]
}];


const GroupsDiscovery = ({ onOpenGroup }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [joinedGroups, setJoinedGroups] = useState([1, 3]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupBio, setNewGroupBio] = useState("");
  const [newGroupCategory, setNewGroupCategory] = useState("Study");
  const [newGroupPrivate, setNewGroupPrivate] = useState(false);
  const [searchMode, setSearchMode] = useState("search");
  const [groupRoles, setGroupRoles] = useState({
    1: { "You": "admin", "Kamran H.": "member" },
    3: { "You": "member", "Nigar A.": "admin" }
  });
  const { toast } = useToast();

  const filtered = allGroups.filter((g) => {
    const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || g.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleJoin = (group) => {
    if (joinedGroups.includes(group.id)) return;
    if (group.isPrivate) {
      setPendingRequests((prev) => [...prev, group.id]);
      toast({ title: "Join request sent" });
    } else {
      setJoinedGroups((prev) => [...prev, group.id]);
      toast({ title: "Joined!", description: `You're now in "${group.name}".` });
    }
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    toast({ title: "Group created!", description: `"${newGroupName}" is ready. Share the link to invite members.` });
    setShowCreateForm(false);
    setNewGroupName("");
    setNewGroupBio("");
  };

  const getStatus = (id) => {
    if (joinedGroups.includes(id)) return "joined";
    if (pendingRequests.includes(id)) return "pending";
    return "none";
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="stat-card !p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-1 rounded-xl bg-muted w-fit">
            <button onClick={() => setSearchMode("search")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "search" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              Search Groups
            </button>
            <button onClick={() => setSearchMode("my")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "my" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              My Groups
            </button>
          </div>
          <button onClick={() => setShowCreateForm(!showCreateForm)}
          className="ml-auto flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex-shrink-0">
            <Plus className="w-3.5 h-3.5" /> Create Group
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchMode === "my" ? "Search my groups..." : "Search groups..."}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          
        </div>

        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat];
            return (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
              selectedCategory === cat ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground hover:text-foreground"}`
              }>
                {Icon && <Icon className="w-3 h-3" />}
                {cat}
              </button>);

          })}
        </div>

        <p className="text-xs text-muted-foreground">{filtered.length} group{filtered.length !== 1 ? "s" : ""} found</p>
      </div>

      {/* Quick Create Form */}
      {showCreateForm &&
      <div className="stat-card !p-4 space-y-3 border-secondary/30 animate-fade-in">
          <h3 className="text-sm font-display font-bold text-foreground flex items-center gap-2">
            <Plus className="w-4 h-4 text-secondary" /> Create a New Group
          </h3>
          <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
        placeholder="Group name"
        className="w-full px-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
        
          <textarea value={newGroupBio} onChange={(e) => setNewGroupBio(e.target.value)}
        placeholder="Short description (optional)"
        className="w-full px-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none h-16" />
        
          <div className="flex gap-2">
            <div className="relative flex-1">
              <select value={newGroupCategory} onChange={(e) => setNewGroupCategory(e.target.value)}
            className="w-full appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
                {categories.filter((c) => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
            <button onClick={() => setNewGroupPrivate(!newGroupPrivate)}
          className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
          newGroupPrivate ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`
          }>
              {newGroupPrivate ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
              {newGroupPrivate ? "Private" : "Public"}
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreateGroup} disabled={!newGroupName.trim()}
          className="flex-1 h-9 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40">
              Create Group
            </button>
            <button onClick={() => setShowCreateForm(false)}
          className="h-9 px-4 rounded-xl bg-muted text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        </div>
      }

      {/* Groups Grid */}
      {searchMode === "search" && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((group) => {
          const status = getStatus(group.id);
          const CatIcon = categoryIcons[group.category];
          return (
            <div key={group.id} className="stat-card !p-4 flex flex-col">
              <div className="flex items-start gap-3 mb-2">
                <Avatar className="w-11 h-11 flex-shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{group.initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-display font-bold text-foreground truncate">{group.name}</span>
                    {group.isPrivate ? <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" /> : <Globe className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {CatIcon && <CatIcon className="w-3 h-3 inline mr-1" />}
                    {group.category}
                    <span className="mx-1">·</span>
                    Created by {group.creator} · {group.createdAt}
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-2 flex-1">{group.bio}</p>

              {group.events.length > 0 &&
              <div className="mb-3 space-y-1.5">
                  {group.events.slice(0, 2).map((event, i) =>
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-xs">
                      <Calendar className="w-3 h-3 text-secondary flex-shrink-0" />
                      <span className="font-medium text-foreground truncate flex-1">{event.title}</span>
                      <span className="text-muted-foreground text-[10px] flex-shrink-0">{event.date.split(",")[0]}</span>
                    </div>
                )}
                </div>
              }

              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {group.members}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {group.events.length} events</span>
              </div>

              <div className="flex gap-2">
                {status === "joined" ?
                <button className="flex-1 h-8 rounded-xl bg-secondary text-secondary-foreground text-xs font-semibold cursor-default">✓ Joined</button> :
                status === "pending" ?
                <button className="flex-1 h-8 rounded-xl bg-chart-5/15 text-chart-5 text-xs font-semibold cursor-default">⏳ Pending</button> :

                <button onClick={() => handleJoin(group)}
                className="flex-1 h-8 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1">
                    {group.isPrivate ? <><Lock className="w-3 h-3" /> Request</> : "Join"}
                  </button>
                }
              </div>
            </div>);

        })}
      </div>}

      {searchMode === "search" && filtered.length === 0 &&
      <div className="stat-card !p-8 text-center">
          <Users className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No groups found</p>
        </div>
      }

      {/* My Groups View */}
      {searchMode === "my" &&
      <div className="space-y-3">
          {(() => {
          const myGroups = allGroups.filter((g) => joinedGroups.includes(g.id));
          const filteredMy = myGroups.filter((g) => g.name.toLowerCase().includes(searchQuery.toLowerCase()));
          if (filteredMy.length === 0) return (
            <div className="stat-card !p-8 text-center">
                <Users className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">You haven't joined any groups yet</p>
              </div>);

          return filteredMy.map((group) => {
            const role = groupRoles[group.id]?.["You"] || "member";
            const members = groupRoles[group.id] || {};
            return (
              <div key={group.id} className="stat-card !p-4">
                  <div className="flex items-start gap-3 mb-2">
                    <Avatar className="w-11 h-11 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{group.initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-display font-bold text-foreground truncate">{group.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                      role === "admin" ? "bg-chart-4/15 text-chart-4" : role === "moderator" ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`
                      }>{role}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{group.category} · {group.members} members · Created by {group.creator}</p>
                    </div>
                  </div>
                  {role === "admin" && Object.keys(members).length > 1 &&
                <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Members & Roles</p>
                      <div className="space-y-1">
                        {Object.entries(members).filter(([name]) => name !== "You").map(([name, memberRole]) =>
                    <div key={name} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/50 text-xs">
                            <span className="text-foreground font-medium">{name}</span>
                            <select value={memberRole}
                      onChange={(e) => {
                        setGroupRoles((prev) => ({ ...prev, [group.id]: { ...prev[group.id], [name]: e.target.value } }));
                        toast({ title: "Role updated", description: `${name} is now ${e.target.value}.` });
                      }}
                      className="appearance-none bg-transparent text-[10px] font-medium text-muted-foreground cursor-pointer focus:outline-none">
                              <option value="member">Member</option>
                              <option value="moderator">Moderator</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                    )}
                      </div>
                    </div>
                }
                </div>);

          });
        })()}
        </div>
      }
    </div>);

};

export default GroupsDiscovery;